import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { getRequestOptions } from 'app/shared/utils/http-request.utils';
import {
  BudgetSegmentAccessResponse,
  BudgetSegmentAmountDO,
  BudgetSegmentDO
} from '../../types/segment.interface';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DeepPartial } from '../../types/deep-partial.type';
import { API_V2_URL, API_V3_URL } from '@common-lib/lib/injection-tokens/url.tokens';
import { SegmentAmountsByTimeframes } from '@shared/types/object-amounts.interface';
import { PfmV3Service } from '../base/pfm-v3.service';
import { ExpenseTotalsBySegments } from '@shared/types/expense-totals-by-segments.type';

@Injectable({
  providedIn: 'root'
})
export class BudgetSegmentService {
  private readonly apiV2Url = inject(API_V2_URL);
  private readonly apiV3Url = inject(API_V3_URL);
  private readonly http = inject(HttpClient);
  private readonly v3_api_manager = inject(PfmV3Service);
  private _segmentExpenseTotals = new BehaviorSubject<ExpenseTotalsBySegments>({});
  public segmentExpenseTotals$ = this._segmentExpenseTotals.asObservable();

  public apiPaths = {
    budgetSegment: 'company_budget_segment/',
    budgetSegmentAmountsWithCAB: 'company_budget_segment/get_segment_amounts/',
    availableSegment: 'available/',
    multiCreate: 'multi_create/',
    multiUpdate: 'multi_update/',
    companyBudgetSegmentAmount: 'company_budget_segment_amount/',
    amountsByTimeframes: 'amounts_by_timeframes/',
    amountsByTimeframesWithCAB: 'amounts_by_timeframes_with_cab/',
    updateSegmentAmountsByTimeframes: 'update_segment_amounts_by_timeframes/',
    updateSegmentForecastAllocationByTimeframes: 'update_forecast_allocation_by_timeframe/',

  };

  getAvailableBudgetSegments(budgetId: number): Observable<BudgetSegmentAccessResponse> {
    const requestUrl =
      `${this.apiV2Url}${this.apiPaths.budgetSegment}${this.apiPaths.availableSegment}`;
    return this.http.get<BudgetSegmentAccessResponse>(
      requestUrl,
      getRequestOptions({ budget: budgetId })).pipe(
        map((data: BudgetSegmentAccessResponse) => {
          data.available_segments = (data.available_segments || []).map(item => ({ ...item, budget: budgetId }));
          return data;
        })
      );
  }

  getBudgetSegments(budgetId: number, isCegBudget: boolean): Observable<BudgetSegmentDO[]> {


    if(!isCegBudget) {
      return this.http.get<BudgetSegmentDO[]>(
        `${this.apiV2Url}${this.apiPaths.budgetSegment}`,
        getRequestOptions({ budget: budgetId })
      );
    }

    // CAB enabled Budget Segments API endpoint

    return this.http.get<BudgetSegmentDO[]>(`${this.apiV2Url}${this.apiPaths.budgetSegmentAmountsWithCAB}`,
      getRequestOptions({ budget: budgetId })
    ).pipe(
      map(segments => {
      const expensesTotalsBySegment: { [budgetId: number]: Record<number, number> } = {};

      const transformedSegments = segments.map(segment => {
        
        const segmentAmounts = segment.amounts
        segmentAmounts.forEach(a => {
          if(a.spent_amount !== 0) {
            if (!expensesTotalsBySegment[segment.id]) {
              expensesTotalsBySegment[segment.id] = {};
            }
            expensesTotalsBySegment[segment.id][a.company_budget_alloc] =  a.spent_amount;
          }
        });
        return { ...segment, amounts: segmentAmounts };
      });
      
      this._segmentExpenseTotals.next(expensesTotalsBySegment);
      return transformedSegments;
    }),
    
    );
  }

  updateBudgetSegment(segmentId, segment: Partial<BudgetSegmentDO>): Observable<any> {
    return this.http.patch(
    `${this.apiV2Url}${this.apiPaths.budgetSegment}${segmentId}/`,
      JSON.stringify(segment)
    );
  }

  deleteBudgetSegment(segmentId: number): Observable<any> {
    return this.http.delete<any>(`${this.apiV2Url}${this.apiPaths.budgetSegment}${segmentId}/`);
  }

  createListOfSegments(segments: DeepPartial<BudgetSegmentDO>[]): Observable<BudgetSegmentDO[]> {
    const { budgetSegment, multiCreate } = this.apiPaths;
    return this.http.post<BudgetSegmentDO[]>(
    `${this.apiV2Url}${budgetSegment}${multiCreate}`,
      JSON.stringify(segments)
    );
  }

  createBudgetSegmentAmounts(values: Partial<BudgetSegmentAmountDO>[]): Observable<BudgetSegmentAmountDO[]> {
    const { companyBudgetSegmentAmount, multiCreate } = this.apiPaths;
    return this.http.post<BudgetSegmentAmountDO[]>(
    `${this.apiV2Url}${companyBudgetSegmentAmount}${multiCreate}`,
      JSON.stringify(values)
    );
  }

  updateBudgetSegmentAmounts(values: Partial<BudgetSegmentAmountDO>[]): Observable<BudgetSegmentAmountDO[]> {
    const { companyBudgetSegmentAmount, multiUpdate } = this.apiPaths;
    return this.http.patch<BudgetSegmentAmountDO[]>(
    `${this.apiV2Url}${companyBudgetSegmentAmount}${multiUpdate}`,
      JSON.stringify(values)
    );
  }

  getAmountsByTimeframes(budgetId: number, ids: number[], params?: object): Observable<SegmentAmountsByTimeframes> {
    
    // Combining Normal and Custom Field Filters with POST request

    let payload = { ...params, budget: budgetId, ids: ids.join(',') };
    
    if(payload['custom_fields']) {
      delete payload['custom_fields'];
    }

    return this.http.post<SegmentAmountsByTimeframes>(
      `${this.apiV3Url}${this.apiPaths.budgetSegment}${this.apiPaths.amountsByTimeframesWithCAB}`,
      { ...payload }
    );

  }

  updateBudgetSegmentAmountsByTimeframe(segmentId: number, data: Partial<BudgetSegmentAmountDO>[]): Observable<BudgetSegmentAmountDO[]> {
    
    let payload = {}; 
    let segmentData = data[0];
    
    if(segmentData['type'] === 'forecast') {

      payload = {
        company_budget_alloc: segmentData['company_budget_alloc'],
        forecast_amount: segmentData['amount']
      }

      // v3 endpoint for updating forecast allocation by timeframes for segments
      return this.v3_api_manager.patch<BudgetSegmentAmountDO[]>(
        `${this.apiPaths.budgetSegment}${segmentId}/${this.apiPaths.updateSegmentForecastAllocationByTimeframes}`,
        payload
      )
    } else {
      delete data[0]['type'];
      payload = {...data[0]}

      // v3 endpoint for updating segment amounts by timeframes for segments
      return this.v3_api_manager.patch<BudgetSegmentAmountDO[]>(
        `${this.apiPaths.budgetSegment}${segmentId}/${this.apiPaths.updateSegmentAmountsByTimeframes}`,
        payload
      )
    }
  }
}
